import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode, FormEvent } from 'react';
import { ApiError } from '../lib/api.js';

// ------------------------------------------------------------------ básicos

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </header>
  );
}

export function Card({
  title,
  description,
  actions,
  children,
  tight,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  tight?: boolean;
}) {
  return (
    <section className="card">
      {title ? (
        <div className="card-head">
          <div>
            <h2>{title}</h2>
            {description ? <div className="small muted">{description}</div> : null}
          </div>
          {actions ? <div className="row">{actions}</div> : null}
        </div>
      ) : null}
      <div className={tight ? 'card-body tight' : 'card-body'}>{children}</div>
    </section>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children}
    </div>
  );
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'attention' | 'critical' | 'ok';
  children: ReactNode;
}) {
  return <span className={`badge ${tone === 'neutral' ? '' : tone}`}>{children}</span>;
}

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warning' | 'error';
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`alert ${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      {title ? <strong>{title}</strong> : null}
      {children}
    </div>
  );
}

/** Mostra erro do servidor com o código de domínio, sem inventar explicação. */
export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const apiError = error instanceof ApiError ? error : null;
  const options = apiError?.details?.options as { name?: string }[] | undefined;
  const changes = apiError?.details?.changes as string[] | undefined;
  return (
    <Alert tone="error" title={apiError ? tituloDoErro(apiError.code) : 'Não foi possível concluir'}>
      <div>{error instanceof Error ? error.message : String(error)}</div>
      {options?.length ? (
        <ul style={{ margin: '8px 0 0 18px' }}>
          {options.map((o, i) => (
            <li key={i}>{o.name}</li>
          ))}
        </ul>
      ) : null}
      {changes?.length ? (
        <ul style={{ margin: '8px 0 0 18px' }}>
          {changes.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      ) : null}
    </Alert>
  );
}

const TITULOS: Record<string, string> = {
  ITEM_AMBIGUOUS: 'Mais de um item corresponde',
  ITEM_NOT_FOUND: 'Item não encontrado',
  PRICE_UNAVAILABLE: 'Preço sob consulta',
  QUOTE_EXPIRED: 'Orçamento vencido',
  QUOTE_STALE: 'Valores mudaram',
  DISCOUNT_NOT_ALLOWED: 'Desconto não autorizado',
  CONDITION_NOT_MET: 'Condição não atendida',
  HUMAN_REQUIRED: 'Precisa de atendimento humano',
  VALIDATION_ERROR: 'Confira os campos',
  NOT_FOUND: 'Registro não encontrado',
  FORBIDDEN: 'Sem permissão',
  UNAUTHENTICATED: 'Sessão encerrada',
  CONFLICT: 'Conflito com um registro existente',
  SLOT_UNAVAILABLE: 'Horário indisponível',
  RATE_LIMITED: 'Muitas tentativas',
  OPT_OUT: 'Contato descadastrado',
  CONSENT_MISSING: 'Falta consentimento',
};

function tituloDoErro(code: string): string {
  return TITULOS[code] ?? 'Não foi possível concluir';
}

// ------------------------------------------------------------------ campos

export function Field({
  label,
  hint,
  children,
  required,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <div className="field">
      <label>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {children}
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

// ------------------------------------------------------------------- modal

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} style={wide ? { width: 'min(920px, 100%)' } : undefined}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Fechar">
            Fechar
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/** Formulário com envio assíncrono, erro visível e botão travado enquanto salva. */
export function useSubmit<T>(action: () => Promise<T>) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault();
      setSaving(true);
      setError(null);
      try {
        return await action();
      } catch (err) {
        setError(err);
        return null;
      } finally {
        setSaving(false);
      }
    },
    [action],
  );

  return { submit, saving, error, setError };
}

// ------------------------------------------------------------------ avisos

interface ToastMessage {
  id: number;
  text: string;
  tone: 'ok' | 'error';
}

const ToastContext = createContext<(text: string, tone?: 'ok' | 'error') => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const push = useCallback((text: string, tone: 'ok' | 'error' = 'ok') => {
    const id = Date.now() + Math.random();
    setMessages((m) => [...m, { id, text, tone }]);
    setTimeout(() => setMessages((m) => m.filter((x) => x.id !== id)), 5000);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        aria-live="polite"
        style={{ position: 'fixed', right: 16, bottom: 16, display: 'grid', gap: 8, zIndex: 80 }}
      >
        {messages.map((m) => (
          <div
            key={m.id}
            className="card"
            style={{
              padding: '11px 15px',
              borderLeft: `4px solid var(${m.tone === 'ok' ? '--gl-success' : '--gl-danger'})`,
              maxWidth: 380,
            }}
          >
            {m.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
